import PageHead from '@/components/PageHead';
import Container from '@/components/Container';
import HeadingOne from '@/components/Heading/HeadingOne';
import PageLayout from '@/components/layout/page-layout';

import NextImage from 'next/image';
import NextLink from 'next/link';

import { FaLinkedin } from 'react-icons/fa';
import { User, Mail } from 'lucide-react';
import { ExtendProps } from 'app';

type AssociateContactProps = ExtendProps<{
	name: string;
	position?: string;
	contacts?: {
		linkedIn: {
			href: string;
			ariaLabel: string;
		};
	};
}>;
function AssociateContact(props: AssociateContactProps) {
	return (
		<div className="flex items-center gap-x-4">
			{props.contacts && props.contacts.linkedIn ? (
				<address>
					<NextLink
						title={props.contacts.linkedIn.ariaLabel}
						tabIndex={0}
						href={props.contacts.linkedIn.href}
						target="_BLANK"
						aria-label={props.contacts.linkedIn.ariaLabel}
						className="bg-[#0a66c2] flex items-center p-1 rounded-full w-max text-white"
					>
						<FaLinkedin fontSize="small" />
					</NextLink>
				</address>
			) : (
				<address>
					<div className="bg-[#0a66c2] flex items-center justify-center p-1 rounded-full text-white [height:28px] [width:28px]">
						<User />
					</div>
				</address>
			)}
			<em tabIndex={0} className="font-bold">
				<span>{props.name}</span>
				<span>{props.position && ` - ${props.position}`}</span>
			</em>
		</div>
	);
}

type ProfileContactProps = ExtendProps<{
	image: {
		src: string;
		label: string;
	};
	profile: {
		name: string;
		position: string;
		contacts?: {
			linkedIn?: {
				href: string;
				ariaLabel: string;
			};
			email?: {
				href: string;
				ariaLabel: string;
			};
		};
	};
}>;

function ProfileContact(props: ProfileContactProps) {
	const readAs = `Foto de ${props.profile.name} - ${props.profile.position}`;

	return (
		<div className="lg:w-[25rem] min-h-[8rem] flex items-center py-4">
			<figure>
				<picture className="" tabIndex={0}>
					<source srcSet={props.image.src} media="(min-width: 0px)" />
					<NextImage
						unoptimized
						alt={props.profile.name}
						src={props.image.src}
						className={`aspect-auto outline-1 outline-primary-dark rounded-full shadow-md shadow-zinc-500`}
						aria-label={readAs}
						title={readAs}
						width={96}
						height={96}
					/>
				</picture>
			</figure>

			<div className="flex flex-col ml-4 mt-2 gap-y-1">
				<p>
					<strong>{props.profile.name}</strong>
					<br />
					<em className="text-sm block [max-width:100%]">
						{props.profile.position}
					</em>
				</p>
				<address className="flex gap-x-2 text-white">
					{props.profile.contacts && props.profile.contacts.linkedIn ? (
						<NextLink
							href={props.profile.contacts.linkedIn.href}
							target="_BLANK"
							aria-label={props.profile.contacts.linkedIn.ariaLabel}
							className="bg-[#0a66c2] flex items-center p-1 rounded-full"
							title={props.profile.contacts.linkedIn.ariaLabel}
						>
							<FaLinkedin fontSize="small" />
						</NextLink>
					) : null}

					{props.profile.contacts && props.profile.contacts.email ? (
						<NextLink
							href={props.profile.contacts.email.href}
							target="_BLANK"
							aria-label={props.profile.contacts.email.ariaLabel}
							title={props.profile.contacts.email.ariaLabel}
							className="bg-[#ED5D5D] flex items-center p-1 rounded-full"
						>
							<Mail fontSize="small" />
						</NextLink>
					) : null}
				</address>
			</div>
		</div>
	);
}

export default function Page() {
	return (
		<>
			<PageHead
				page={{
					title: 'Instituto Deixe Vivo - Quem faz acontecer',
					keywords:
						'OSC, Instituto, Deixe Vivo, Doação de órgãos e tecidos, voluntário, voluntária, voluntariado, voluntários, voluntárias, terceiro setor, gestão',
					colorScheme: 'light dark',
					description:
						'Conheça a equipe que trabalha para cumprir a missão do Instituto Deixe Vivo',
					robots: 'follow, index',
				}}
				og={{
					description:
						'Conheça a equipe que trabalha para cumprir a missão do Instituto Deixe Vivo',

					site_name: 'Instituto Deixe Vivo - Quem faz acontecer',
					title: 'Instituto Deixe Vivo - Quem faz acontecer',
					type: 'website',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						fbAdmins: 'instituto.deixevivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
						type: 'image/jpg',
						width: '1200',
						height: '1200',
					},
				}}
				twitter={{
					card: 'summary',
					description:
						'Conheça a equipe que trabalha para cumprir a missão do Instituto Deixe Vivo',

					site: 'Instituto Deixe Vivo - Quem faz acontecer',
					title: 'Instituto Deixe Vivo - Quem faz acontecer',
					url: 'https://deixevivo.org.br',
					image: {
						alt: 'Logo do Instituto, ao lado esquerdo da imagem um rosto feliz e ao lado direito texto que diz Instituto Deixe Vivo',
						src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681919729/InstitutoDeixeVivo/logos/logo-deixe-vivo-1200x1200.jpg',
					},
				}}
			/>

			<PageLayout>
				<Container className="py-8">
					<HeadingOne horizontalRow>Quem faz acontecer</HeadingOne>
					<div className="my-4">
						<p tabIndex={0}>
							Conheça a equipe que trabalha para cumprir a missão do Instituto
							Deixe Vivo:
						</p>
					</div>

					<div className="flex flex-wrap gap-x-12">
						<section>
							<h2 className="text-2xl" tabIndex={0}>
								Diretoria
							</h2>

							<div className="flex gap-x-12 flex-wrap">
								<ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681245466/InstitutoDeixeVivo/equipe/Giovanna_Fiori.jpg',
										label: '',
									}}
									profile={{
										name: 'Giovanna Camargo Fiori',
										position: 'Diretora e Presidente',
										contacts: {
											linkedIn: {
												href: 'https://www.linkedin.com/in/giovanna-fiori/',
												ariaLabel:
													'Acessar o LinkedIn de Giovanna Camargo Fiori',
											},
											email: {
												href: 'mailto:giovanna@deixevivo.org.br',
												ariaLabel: 'Enviar e-mail para Giovanna Camargo Fiori',
											},
										},
									}}
								/>

								<ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681245467/InstitutoDeixeVivo/equipe/Olga_Schekiera_1.jpg',
										label: 'Olga Schekiera',
									}}
									profile={{
										name: 'Olga Schekiera',
										position: 'Diretora Financeira',
										contacts: {
											linkedIn: {
												href: 'https://www.linkedin.com/in/olga-brigida-schekiera-44219b125/',
												ariaLabel: 'Acessar o LinkedIn de Olga Schekiera',
											},
											email: {
												href: 'mailto:olga@deixevivo.org.br',
												ariaLabel: 'Enviar e-mail para Olga Schekiera',
											},
										},
									}}
								/>
							</div>
						</section>

						{/* <section className="w-full lg:w-[unset]">
							<h2 className="text-2xl" tabIndex={0}>
								Diretoria
							</h2>

							
						</section> */}
					</div>

					<div>
						<section className="mt-4">
							<h2 className="text-2xl" tabIndex={0}>
								Voluntárias Recorrentes
							</h2>
							<div className="flex gap-x-12 flex-wrap">
								{/* <ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681245467/InstitutoDeixeVivo/equipe/Chayene_Gomes.jpg',
										label: 'Chayene Gomes',
									}}
									profile={{
										name: 'Chayene Gomes',
										position: 'Comunicação e Conteúdo',
										contacts: {
											linkedIn: {
												href: 'https://www.linkedin.com/in/chayene-gomes-83535a84',
												ariaLabel: 'Acessar o LinkedIn de Chayene Gomes',
											},
										},
									}}
								/> */}

								{/* <ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681245466/InstitutoDeixeVivo/equipe/Daniele_Meireles.jpg',
										label: 'Daniele Meireles',
									}}
									profile={{
										name: 'Daniele Meireles',
										position: 'Comunicação Ações, Eventos e Campanhas',
										contacts: {
											linkedIn: {
												href: 'https://www.linkedin.com/in/danielemeireles/',
												ariaLabel: 'Acessar o LinkedIn de Daniele Meireles',
											},
											email: {
												href: 'mailto:conteudo@deixevivo.org.br',
												ariaLabel: 'Enviar e-mail para Daniele Meireles',
											},
										},
									}}
								/> */}

								<ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681245467/InstitutoDeixeVivo/equipe/Denise_Critis.png',
										label: 'Denise Critis',
									}}
									profile={{
										name: 'Denise Critis',
										position: 'Ações, Eventos e Campanhas',
										contacts: {
											email: {
												href: 'mailto:eventos@deixevivo.org.br',
												ariaLabel: 'Enviar e-mail para Denise Critis',
											},
										},
									}}
								/>

								<ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681245468/InstitutoDeixeVivo/equipe/Kelly_Martins.jpg',
										label: 'Kelly Martins',
									}}
									profile={{
										name: 'Kelly Martins',
										position: 'Comunicação e Conteúdo',
										contacts: {
											linkedIn: {
												href: 'https://www.linkedin.com/in/kelly-martins-96629542',
												ariaLabel: 'Acessar o LinkedIn de Kelly Martins',
											},
										},
									}}
								/>

								{/* <ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681245466/InstitutoDeixeVivo/equipe/Let%C3%ADcia_Amorim.jpg',
										label: 'Letícia Amorim',
									}}
									profile={{
										name: 'Letícia Amorim',
										position: 'Rede de Acolhimento',
										contacts: {
											linkedIn: {
												href: 'https://www.linkedin.com/in/let%C3%ADcia-amorim-lasmar-rh/',
												ariaLabel: 'Acessar o LinkedIn de Letícia Amorim',
											},
											email: {
												href: 'mailto:acolhimento@deixevivo.org.br',
												ariaLabel: 'Enviar e-mail para Letícia Amorim',
											},
										},
									}}
								/> */}

								{/* <ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681245467/InstitutoDeixeVivo/equipe/Let%C3%ADcia_Beserra.jpg',
										label: 'Letícia Beserra',
									}}
									profile={{
										name: 'Letícia Beserra',
										position: 'Comunicação e Conteúdo',
										contacts: {
											linkedIn: {
												href: 'https://www.linkedin.com/in/leticiabeserra/',
												ariaLabel: 'Acessar o LinkedIn de Letícia Beserra',
											},
										},
									}}
								/> */}

								{/* <ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681245468/InstitutoDeixeVivo/equipe/Marcia_Rebelo.jpg',
										label: 'Marcia Rebelo',
									}}
									profile={{
										name: 'Marcia Rebelo',
										position: 'Consultora de Projetos',
										contacts: {
											linkedIn: {
												href: 'https://www.linkedin.com/in/marcia-rebelo-81b521157',
												ariaLabel: 'Acessar o LinkedIn de Marcia Rebelo',
											},
										},
									}}
								/> */}

								{/* <ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1681245467/InstitutoDeixeVivo/equipe/Marina_Spinardi.jpg',
										label: 'Marina Spinardi',
									}}
									profile={{
										name: 'Marina Spinardi',
										position: 'Gestora de Voluntariado',
										contacts: {
											email: {
												href: 'mailto:voluntariado@deixevivo.org.br',
												ariaLabel: 'Enviar e-mail para Marina Spinardi',
											},
										},
									}}
								/> */}

								<ProfileContact
									image={{
										src: 'https://res.cloudinary.com/oliveiradev/image/upload/v1690753012/InstitutoDeixeVivo/equipe/foto-mayara.jpg',
										label: 'Mayara Fernandes',
									}}
									profile={{
										name: 'Mayara Fernandes',
										position: 'Gestora de Comunicação e Conteúdo',
										contacts: {
											linkedIn: {
												href: 'https://www.linkedin.com/in/mayara-fernandes-de-freitas/',
												ariaLabel: 'Acessar o LinkedIn de Mayara Fernandes',
											},
											email: {
												href: 'mailto:mayara@deixevivo.org.br',
												ariaLabel: 'Enviar e-mail para Mayara Fernandes',
											},
										},
									}}
								/>
							</div>
						</section>
					</div>

					<section className="mt-4">
						<h2 className="text-2xl" tabIndex={0}>
							Associados Fundadores
						</h2>
						<div className="flex flex-col gap-y-4 my-4">
							<AssociateContact
								name="Daniele Meireles"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/danielemeireles/',
										ariaLabel: 'Acessar o LinkedIn de Daniele Meireles',
									},
								}}
							/>
							<AssociateContact
								name="Diego de Carvalho Souza Lima"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/diegocslima/',
										ariaLabel:
											'Acessar o LinkedIn de Diego de Carvalho Souza Lima',
									},
								}}
							/>

							<AssociateContact
								name="Giovanna Camargo Fiori"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/giovanna-fiori/',
										ariaLabel: 'Acessar o LinkedIn de Giovanna Camargo Fiori',
									},
								}}
							/>

							<AssociateContact
								name="Isabella Camargo Fiori"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/isabellafiori/',
										ariaLabel: 'Acessar o LinkedIn de Isabella Camargo Fiori',
									},
								}}
							/>
							<AssociateContact name="Marcia Camargo do Amaral Fiori" />

							<AssociateContact
								name="Marcia Cristina José Rebelo"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/marcia-rebelo-81b521157',
										ariaLabel:
											'Acessar o LinkedIn de Marcia Cristina José Rebelo',
									},
								}}
							/>

							<AssociateContact
								name="Mayara Fernandes de Freitas"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/mayara-fernandes-de-freitas/',
										ariaLabel:
											'Acessar o LinkedIn de Mayara Fernandes de Freitas',
									},
								}}
							/>

							<AssociateContact
								name="Olga Brígida Schekiera"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/olga-brigida-schekiera-44219b125/',
										ariaLabel: 'Acessar o LinkedIn de Olga Brígida Schekiera',
									},
								}}
							/>
						</div>
					</section>

					<section className="mt-4">
						<h2 className="text-2xl" tabIndex={0}>
							Membros do Conselho <time dateTime="01-01-2023">2023</time>
							&nbsp;-&nbsp;<time dateTime="01-01-2023">2027</time>
						</h2>
						<div className="flex flex-col gap-y-2 my-4">
							<AssociateContact
								name="Giovanna Camargo Fiori"
								position="Diretora Presidente"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/giovanna-fiori/',
										ariaLabel: 'Acessar o LinkedIn de Giovanna Camargo Fiori',
									},
								}}
							/>

							<AssociateContact
								name="Diego de Carvalho Souza Lima"
								position="Diretor Vice Presidente"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/diegocslima/',
										ariaLabel:
											'Acessar o LinkedIn de Diego de Carvalho Souza Lima',
									},
								}}
							/>

							<AssociateContact
								name="Olga Brígida Schekiera"
								position="Diretora Financeira"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/olga-brigida-schekiera-44219b125/',
										ariaLabel: 'Acessar o LinkedIn de Olga Brígida Schekiera',
									},
								}}
							/>

							<AssociateContact
								name="Lilia Aparecida Rodrigues de Souza Santos"
								position="Conselheira Fiscal Presidente"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/lilia-souza-santos-souza-santos-ab54b533/',
										ariaLabel:
											'Acessar o LinkedIn de Lilia Aparecida Rodrigues de Souza Santos',
									},
								}}
							/>

							<AssociateContact
								name="Raphael Dardis Rodrigues Lopes"
								position="Conselheiro Fiscal"
								contacts={{
									linkedIn: {
										href: 'https://www.linkedin.com/in/raphael-dardis',
										ariaLabel:
											'Acessar o LinkedIn de Raphael Dardis Rodrigues Lopes',
									},
								}}
							/>
						</div>
					</section>
				</Container>
			</PageLayout>
		</>
	);
}
